import { useQuery } from "@tanstack/react-query";
import { fetchReviews } from "@/lib/api";

/**
 * [Phase 26] Live Google rating for sitewide trust chips.
 *
 * Reads the backend's short-TTL Places snapshot via GET /api/reviews.
 * Never throws — while loading or when the feed is unavailable it returns
 * ``{ available: false }`` so callers fall back to neutral copy (the old
 * hardcoded "5★ on Google" claim is gone for good).
 */
export function useGoogleRating(lang = "en") {
  const { data } = useQuery({
    queryKey: ["google-reviews", lang],
    queryFn: () => fetchReviews(lang),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  if (data?.available && data.ratingValue && data.reviewCount) {
    return {
      available: true,
      rating: data.ratingValue,
      count: data.reviewCount,
      googleMapsUri: data.googleMapsUri || "",
    };
  }
  return { available: false };
}

/**
 * Format the trust-chip label from live data, falling back to the neutral
 * UI string when the feed is unavailable.
 * EN: "4.3★ on Google (323 reviews)" / ES: "4.3★ en Google (323 reseñas)"
 */
export function googleChipLabel(live, lang, fallback) {
  if (!live?.available) return fallback;
  const r = live.rating.toFixed(1);
  return lang === "es"
    ? `${r}★ en Google (${live.count} reseñas)`
    : `${r}★ on Google (${live.count} reviews)`;
}
